import { useState } from 'react';
import styles from './LoginPage.module.css';
import { Link, Box, Modal, Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useUserContext } from '../../../../Context/UserContext';
import { useLoaderContext, useToastContext } from '../../../../Context/ToastContext';
import Cookies from "js-cookie";
import navbarLogoDesktop from '../../../../images/logo_loginscreen.png';
import navbarLogoMobile from '../../../../images/login_mobile.png';
import { apiPost } from '../../../../utils/ApiManager';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const adminLogin = process.env.REACT_APP_ADMIN_LOGIN
const handlePassword = process.env.REACT_APP_HANDLE_RESET_PASSWORD

const LoginPage = () => {
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [errors, setErrors] = useState({
        username: '',
        password: '',
    });
    const [isFormValid, setIsFormValid] = useState(false);
    const { loading, setLoading } = useLoaderContext();
    const [modalEmailError, setModalEmailError] = useState('');
    const [email, setEmail] = useState("");
    const [isModalEmailValid, setIsModalEmailValid] = useState(false);
    const { showToast } = useToastContext();

    const navigate = useNavigate();

    // Get the login function from context
    const { login } = useUserContext();

    // Form Validation on input change
    const handleValidation = (fieldName, value) => {
        const newErrors = { ...errors };
        let valid = true;

        if (fieldName === 'username') {
            if (!value.trim()) {
                newErrors.username = 'Username is required';
                valid = false;
            } else {
                const usernameError = validateUsername(value);
                if (usernameError) {
                    newErrors.username = usernameError;
                    valid = false;
                } else {
                    newErrors.username = ''; // Clear error if valid
                }
            }
        }

        if (fieldName === 'password') {
            if (!value.trim()) {
                newErrors.password = 'Password is required';
                valid = false;
            } else {
                newErrors.password = ''; // Clear error if valid
            }
        }

        setErrors(newErrors);
        setIsFormValid(valid);
    };

    const validateUsername = (username) => {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        const phoneRegex = /^[0-9]{10}$/;

        if (emailRegex.test(username)) {
            return ''; // Valid email
        } else if (phoneRegex.test(username)) {
            return ''; // Valid phone number
        } else {
            return 'Please enter a valid email or phone number';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));

        handleValidation(name, value); // Validate each field on input change
    };

    const handleModalEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        const validateEmail = (email) => {
            if (email.trim() === '') {
                return 'Email is required';
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email) ? '' : 'Invalid email format';
        };

        const validationError = validateEmail(value);
        if (validationError) {
            setModalEmailError(validationError);
            setIsModalEmailValid(false);
        } else {
            setModalEmailError('');
            setIsModalEmailValid(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        handleValidation('username', formData.username); // Validate on submit for username
        handleValidation('password', formData.password); // Validate on submit for password

        // Prevent submission if the form is invalid
        if (!isFormValid) return;

        try {
            const response = await apiPost(adminLogin, formData);
            if(response?.success){
                const token = response?.token;
                Cookies.set('token', token);
                localStorage.setItem("profileImage", response?.profileImage);
                if(token){
                    const decodedToken = jwtDecode(token);
                    const role = decodedToken?.role;
                    login(decodedToken);
                }
                showToast(response?.message || 'Login successful', 'success');
                navigate('/');
            }else{
                showToast(response?.message || 'Login failed', 'error');
            }
        } catch (error) {
            showToast(error?.response?.message || error?.message || 'An error occurred. Please try again.', 'error');
        }
        finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!isModalEmailValid) {
            return;
        }

        try {
            setLoading(true);
            const response = await apiPost(handlePassword, { email: email });
            if (response?.success) {
                showToast( response?.message || "Please share the OTP sent to your email !", "success");
                navigate('/updatePassword', { state: { username: email } });
            }else{
                showToast(response?.message || "Something went wrong !", "error");
            }
        } catch (error) {
            showToast(error?.response?.message || error?.message || "Something went wrong !", "error");
        }
        finally {
            setLoading(false);
        }
    };


    return (
        <div className={styles.mainContainer}>
            {/* Login Form */}
            <div className={styles.main}>
                <Box className={styles.leftContainer}>
                    <img src={navbarLogoDesktop} className={`${styles.navbarLogo} ${styles.desktopLogo}`} alt="desktop logo" />
                    <img src={navbarLogoMobile} className={`${styles.navbarLogo} ${styles.mobileLogo}`} alt="mobile logo" />
                    <h1>Your Taste, Our Promise.</h1>
                </Box>

                <div className={styles.rightContainer}>
                    <Typography variant="h5" sx={{ color: 'black' }} className={styles.rightContainerTitle}>
                        Login
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.loginGroup}>
                            <label htmlFor="username">Email or Phone</label>
                            <input
                                type="text"
                                id="username"
                                placeholder="Username"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                            />
                            {errors.username && <span className={styles.error}>{errors.username}</span>}
                        </div>
                        <div className={styles.loginGroup}>
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                placeholder="Password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                            />
                            {errors.password && <span className={styles.error}>{errors.password}</span>}
                        </div>

                        <div className={styles.loginGroup}>
                            <Button sx={{ backgroundColor: '#207d46' }} variant="contained" type="submit" className={styles.btnLogin} disabled={!isFormValid}>
                                Login
                            </Button>
                        </div>

                        <Grid className={styles.forgotPassword}>
                            <Link href="#" className={styles.link} onClick={() => setOpenModal(true)}>
                                Forgot password?
                            </Link>
                        </Grid>

                        <Modal open={openModal} onClose={() => setOpenModal(false)}>
                            <Box className={styles.modalBackground}>
                                <Box className={styles.forgotPassModal}>
                                    <div
                                        onClick={() => setOpenModal(false)}
                                        className={styles.backIconContainer}
                                    >
                                        <ArrowBackIcon className={styles.backIcon} />
                                    </div>
                                    <Typography variant="h5" className={styles.editContainerTitle}>
                                        Forgot Password
                                    </Typography>
                                    <Typography variant="body2" mt={1}>
                                        Enter your email to receive OTP.
                                    </Typography>
                                    <Box className={styles.loginGroup}>
                                        <input
                                            type="text"
                                            id="modalUsername"
                                            placeholder="Email"
                                            name="email"
                                            value={email}
                                            onChange={handleModalEmailChange} />
                                        {modalEmailError && <span className={styles.error}>{modalEmailError}</span>}
                                    </Box>
                                    <Box className={styles.loginGroup}>
                                        <Button sx={{ backgroundColor: '#207d46' }} variant="contained" type="button" onClick={handleResetPassword} className={styles.btnReset} disabled={!isModalEmailValid}>
                                            Reset Password
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Modal>
                    </form>
                </div>
            </div>

            {/* <div className={styles.footer}>
                <p>Copyright © {new Date().getFullYear()} DiNEST. All rights reserved.</p>
            </div> */}
        </div >
    );
};

export default LoginPage;
